import React, { useEffect, useState } from "react";
import { listSellerRFQs, approveRFQ, rejectRFQ } from "../../api/sellerRFQ.api";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { Input, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Label, Tooltip } from "reactstrap";
import StatusBadge from "../../components/StatusBadge";

const SellerRFQs = () => {
  const [data, setData] = useState([]);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [price, setPrice] = useState("");
  const [tooltips, setTooltips] = useState({
    viewDetails: {}, // Map of rowId to boolean
    reject: false,
    approve: false,
    close: false
  });

  const toggleTooltip = (tooltipName, rowId = null) => {
    setTooltips(prev => {
      if (rowId !== null && typeof prev[tooltipName] === 'object') {
        return {
          ...prev,
          [tooltipName]: {
            ...prev[tooltipName],
            [rowId]: !prev[tooltipName][rowId]
          }
        };
      } else {
        return {
          ...prev,
          [tooltipName]: !prev[tooltipName]
        };
      }
    });
  };

  // --- LOGIC FUNCTIONS (UNCHANGED) ---
  const fetchData = async () => {
    const res = await listSellerRFQs({ skip: 0, per_page: 100, match: query });
    if (res.data.data.length > 0) { setData(res.data.data[0].data); } else { setData([]); }
  };
  useEffect(() => { fetchData(); }, [query]);

  const handleApprove = async () => { 
     if (!selectedRFQ) return;
     
     if(!price || price.trim() === "") {
       toast.error("Please enter a quotation price");
       return;
     }

     await approveRFQ(selectedRFQ.id, {price});
     toast.success("Approved"); 
     setModal(false);
     setPrice("");
     fetchData(); 
  };

  const handleReject = async () => { 
    if (!selectedRFQ) return;
    await rejectRFQ(selectedRFQ.id); 
    toast.success("Rejected"); 
    setModal(false);
    fetchData(); 
  };

  const openDetailsModal = (row) => {
    setSelectedRFQ(row);
    setPrice("");
    setModal(true);
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // --- DESIGN STYLES ---
  const columns = [
    { name: "Product Name", selector: r => r.product_name, grow: 1.5, style: { fontWeight: "700" } },
    { name: "Buyer Company", selector: r => r.buyer_name },
    { name: "Quantity", selector: r => r.quantity, center: true, style: { fontWeight: "600" } },
    { name: "Current Status", center: true, cell: r => <StatusBadge status={r.status} /> },
    {
      name: "Quick Actions",
      right: true,
      grow: 1.5,
      cell: (row) => (
        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            id={`viewDetails-${row.id}`}
            onClick={() => openDetailsModal(row)}
            style={{
              border: "none",
              background: "transparent",
              color: "#112347",
              fontSize: "13px",
              fontWeight: "600",
              whiteSpace: "nowrap",
              cursor: "pointer"
            }}
          >
            View Details →
          </button>
          <Tooltip 
            placement="top" 
            isOpen={tooltips.viewDetails[row.id] || false} 
            target={`viewDetails-${row.id}`} 
            toggle={() => toggleTooltip('viewDetails', row.id)}
          >
            View complete RFQ details and submit quotation
          </Tooltip>
        </div>
      )
    }
  ];

  return (
    <div style={{ backgroundColor: "#ffffff", padding: "clamp(15px, 3vw, 30px)", borderRadius: "12px", border: "1px solid #e5e7eb" }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <h4 className="fw-bold m-0" style={{ color: "#112347" }}>Request for Quotations</h4>
        <div className="position-relative flex-grow-1 flex-md-grow-0" style={{ minWidth: "250px", maxWidth: "350px" }}>
          <span className="position-absolute translate-middle-y top-50 ms-3 text-muted">🔍</span>
          <Input
            placeholder="Search RFQs..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ backgroundColor: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", paddingLeft: "40px", height: "45px" }}
          />
        </div>
      </div>

      {/*  table-responsive wrapper added */}
      <div className="table-responsive border rounded-3 overflow-hidden">
        <DataTable columns={columns} data={data} pagination />
      </div>

      {/*  DETAILS MODAL */}
      <Modal isOpen={modal} toggle={() => { setModal(false); setPrice(""); }} size="md" centered backdrop="static">
        <ModalHeader toggle={() => setModal(false)} className="border-0 pb-0">
          <h4 className="fw-bold m-0" style={{ color: "#112347" }}>RFQ Details</h4>
        </ModalHeader>

        <ModalBody className="pt-4 px-4">
          {selectedRFQ && (
            <div>
              <Row className="mb-3">
                <Col md="4">
                  <Label className="small fw-bold text-muted">Buyer Name:</Label>
                  <div className="mt-1" style={{ fontSize: "15px",  color: "#374151" }}>
                    {selectedRFQ.buyer_name || "N/A"}
                  </div>
                </Col>
                <Col md="8">
                  <Label className="small fw-bold text-muted">Product:</Label>
                  <div className="mt-1" style={{ fontSize: "15px",  color: "#374151" }}>
                    {selectedRFQ.product_name || "N/A"}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="6">
                  <Label className="small fw-bold text-muted">Quantity Requested:</Label>
                  <div className="mt-1" style={{ fontSize: "15px", color: "#374151" }}>
                    {selectedRFQ.quantity || "N/A"} units
                  </div>
                </Col>
                <Col md="6">
                  <Label className="small fw-bold text-muted">Current Status:</Label>
                  <div className="mt-1">
                    <StatusBadge status={selectedRFQ.status} />
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="12">
                  <Label className="small fw-bold text-muted">RFQ Sent Date & Time:</Label>
                  <div className="mt-1" style={{ fontSize: "15px", color: "#374151" }}>
                    {formatDate(selectedRFQ.created_at || selectedRFQ.sent_at)}
                  </div>
                </Col>
              </Row>

              {selectedRFQ.status !== "PENDING" && selectedRFQ.price && (
                <Row className="mb-3">
                  <Col md="12">
                    <Label className="small fw-bold text-muted">Quotation Price:</Label>
                    <div className="mt-1" style={{ fontSize: "15px", color: "#059669" }}>
                      {selectedRFQ.price}
                    </div>
                  </Col>
                </Row>
              )}

              {selectedRFQ.notes && (
                <Row className="mb-3">
                  <Col md="12">
                    <Label className="small fw-bold text-muted">Buyer Notes:</Label>
                    <div className="mt-1 p-2" style={{ fontSize: "14px", color: "#4b5563", backgroundColor: "#f9fafb", borderRadius: "6px", minHeight: "60px" }}>
                      {selectedRFQ.notes}
                    </div>
                  </Col>
                </Row>
              )}

              {selectedRFQ.status === "PENDING" && (
                <Row className="mb-3">
                  <Col md="12">
                    <Label htmlFor="priceInput" className="small fw-bold text-muted">Quotation Price:</Label>
                    <Input
                      id="priceInput"
                      type="text"
                      placeholder="Enter your quotation price"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      style={{ marginTop: "8px", borderRadius: "6px", padding: "10px 12px" }}
                    />
                  </Col>
                </Row>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter style={{ borderTop: "1px solid #e5e7eb", padding: "20px 24px", gap: "10px" }}>
          {selectedRFQ && selectedRFQ.status === "PENDING" && (
            <>
              <button
                id="rejectBtn"
                onClick={handleReject}
                style={{
                  backgroundColor: "#ffffff",
                  color: "#dc2626",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  flex: 1
                }}
              >
                Reject
              </button>
              <Tooltip placement="top" isOpen={tooltips.reject} target="rejectBtn" toggle={() => toggleTooltip('reject')}>
                Decline this RFQ and notify the buyer
              </Tooltip>
              <button
                id="approveBtn"
                onClick={handleApprove}
                style={{
                  backgroundColor: "#112347",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  flex: 1
                }}
              >
                Approve
              </button>
              <Tooltip placement="top" isOpen={tooltips.approve} target="approveBtn" toggle={() => toggleTooltip('approve')}>
                Submit quotation price and approve this RFQ
              </Tooltip>
            </>
          )}
          {selectedRFQ && selectedRFQ.status !== "PENDING" && (
            <>
              <button
                id="closeBtn"
                onClick={() => setModal(false)}
                style={{
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  width: "100%"
                }}
              >
                Close
              </button>
              <Tooltip placement="top" isOpen={tooltips.close} target="closeBtn" toggle={() => toggleTooltip('close')}>
                Close this RFQ details window
              </Tooltip>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};
export default SellerRFQs;